import { useCallback, useEffect, useRef, useState } from "react";

export const BUILDER_MENU_PANEL_SLIDE_MS = 250;

interface IUseBuilderMenuPanelStateOptions<T extends string> {
  activeId: T;
  setActiveId: (id: T) => void;
  noneId: T;
  slideMs?: number;
  skipCloseAnimation?: () => boolean;
  blockActionsWhileClosing?: boolean;
}

interface IUseBuilderMenuPanelStateResult<T extends string> {
  panelOpen: boolean;
  closing: boolean;
  closingFromActiveId: T | null;
  requestSetActiveId: (id: T) => void;
  isSelected: (id: T) => boolean;
}

export function useBuilderMenuPanelState<T extends string>(
  options: IUseBuilderMenuPanelStateOptions<T>,
): IUseBuilderMenuPanelStateResult<T> {
  const {
    activeId,
    setActiveId,
    noneId,
    slideMs = BUILDER_MENU_PANEL_SLIDE_MS,
    skipCloseAnimation,
    blockActionsWhileClosing = true,
  } = options;

  const [closing, setClosing] = useState(false);
  const [closingFromActiveId, setClosingFromActiveId] = useState<T | null>(null);
  const closeTimeoutRef = useRef<number | null>(null);
  const closingFromIdRef = useRef<T | null>(null);

  const clearClosing = useCallback(() => {
    if (closeTimeoutRef.current !== null) {
      window.clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
    closingFromIdRef.current = null;
    setClosingFromActiveId(null);
    setClosing(false);
  }, []);

  const requestSetActiveId = useCallback(
    (id: T) => {
      if (id !== noneId) {
        if (closing && blockActionsWhileClosing) {
          return;
        }
        clearClosing();
        setActiveId(id);
        return;
      }

      if (closing || activeId === noneId) {
        return;
      }

      if (skipCloseAnimation?.()) {
        clearClosing();
        setActiveId(noneId);
        return;
      }

      closingFromIdRef.current = activeId;
      setClosingFromActiveId(activeId);
      setClosing(true);
      closeTimeoutRef.current = window.setTimeout(() => {
        setActiveId(noneId);
        closeTimeoutRef.current = null;
        closingFromIdRef.current = null;
        setClosingFromActiveId(null);
        setClosing(false);
      }, slideMs);
    },
    [
      activeId,
      blockActionsWhileClosing,
      clearClosing,
      closing,
      noneId,
      setActiveId,
      skipCloseAnimation,
      slideMs,
    ],
  );

  useEffect(() => {
    if (!closing || closingFromIdRef.current === null || blockActionsWhileClosing) {
      return;
    }

    if (activeId !== closingFromIdRef.current && activeId !== noneId) {
      clearClosing();
    }
  }, [activeId, blockActionsWhileClosing, clearClosing, closing, noneId]);

  useEffect(() => {
    return () => {
      if (closeTimeoutRef.current !== null) {
        window.clearTimeout(closeTimeoutRef.current);
      }
    };
  }, []);

  const panelOpen = activeId !== noneId && !closing;

  const isSelected = useCallback(
    (id: T) => id === activeId,
    [activeId],
  );

  return {
    panelOpen,
    closing,
    closingFromActiveId,
    requestSetActiveId,
    isSelected,
  };
}
